import { AutoAwesomeOutlined, Check, Close } from '@mui/icons-material'
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  darken,
  Dialog,
  IconButton,
  List,
  ListItem,
  Typography,
  useTheme,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

import getBenefits from '~/components/upgradeToUnlock/utils'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { EClientPath } from '~/models/api'
import rem, { getWhiteOrBlackTextColorByBackground } from '~/utils/ui'

type TProps = {
  isOpen: boolean
  onClose: () => void
}

function UpgradeToUnlock({ isOpen, onClose }: TProps) {
  const theme = useTheme()
  const navigate = useNavigate()
  const isPartner = useIsPartner()

  const { partner } = usePartnerCentralized()

  return (
    <Dialog
      sx={{
        zIndex: theme.zIndex.tooltip + 1,
        '.MuiDialog-paper': {
          marginBlock: 0,
          marginInline: rem(16),
          width: '100%',
          maxWidth: rem(500),
          borderRadius: rem(8),
        },
      }}
      open={isOpen}
      onClose={onClose}
    >
      <Card>
        <CardHeader
          action={
            <IconButton
              onClick={onClose}
              sx={{
                color: theme.palette.common.white,
                padding: 0,
              }}
            >
              <Close sx={{ fontSize: rem(16) }} />
            </IconButton>
          }
          title="Upgrade to unlock this feature"
          subheader="Get access to premium features and take your AI assistant to the next level"
          sx={{
            paddingInline: rem(32),
            paddingTop: rem(44),
            paddingBottom: rem(60),
            background: isPartner
              ? `linear-gradient(to right, ${theme.palette.tertiary1} 0%, ${darken(theme.palette.tertiary1, 0.26)} 100%)`
              : `linear-gradient(to right, ${theme.palette.menuBackground} 0%, ${darken(theme.palette.tertiary1, 0.26)} 100%)`,
            '.MuiCardHeader-action': {
              marginTop: rem(-24),
              marginRight: rem(-16),
            },
            '.MuiCardHeader-content': {
              display: 'flex',
              flexDirection: 'column',
              gap: rem(8),
            },
            '.MuiCardHeader-title': {
              fontWeight: 700,
              fontSize: rem(26),
              color: theme.palette.common.white,
            },
            '.MuiCardHeader-subheader': {
              color: theme.palette.common.white,
              opacity: 0.75,
            },
          }}
        />
        <CardContent
          sx={{
            position: 'relative',
            paddingInline: rem(32),
            paddingTop: rem(80),
          }}
        >
          <AutoAwesomeOutlined
            sx={{
              position: 'absolute',
              top: 0,
              transform: 'translateY(-50%)',
              backgroundColor: theme.palette.primaryButtonBg,
              color: getWhiteOrBlackTextColorByBackground(
                theme.palette.primaryButtonBg,
                theme,
              ),
              padding: rem(16),
              fontSize: rem(84),
              borderRadius: rem(16),
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: rem(14),
              textTransform: 'uppercase',
            }}
          >
            Included in upgrades
          </Typography>
          <List
            dense
            sx={{
              padding: 0,
              marginTop: rem(16),
              display: 'flex',
              flexDirection: 'column',
              gap: rem(8),
            }}
          >
            {getBenefits(partner.partnerName).map((benefit) => (
              <ListItem key={benefit} sx={{ padding: 0, gap: rem(8) }}>
                <Check
                  sx={{
                    backgroundColor: theme.palette.primaryButtonBg,
                    color: getWhiteOrBlackTextColorByBackground(
                      theme.palette.primaryButtonBg,
                      theme,
                    ),
                    borderRadius: '50%',
                    padding: rem(2),
                    fontSize: rem(18),
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: rem(16),
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  {benefit}
                </Typography>
              </ListItem>
            ))}
          </List>
        </CardContent>
        <CardActions
          sx={{
            padding: rem(32),
            gap: rem(8),
            '> button': {
              flex: 1,
              borderRadius: rem(8),
              letterSpacing: 0,
              fontSize: rem(16),
            },
          }}
        >
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={() =>
              navigate(isPartner ? EClientPath.account : EClientPath.billing)
            }
          >
            {isPartner ? 'Learn More' : 'View Plans'}
          </Button>
        </CardActions>
      </Card>
    </Dialog>
  )
}

export default UpgradeToUnlock
